/**
 * Round-trip helpers between HTML (Quill editor's storage format) and plain
 * text (what we want to show in the Apply My Style modal and what we want
 * to send to the LLM for rewriting).
 *
 * Sprint 5 #155 — the modal originally rendered raw HTML markup in its
 * before/after panels and computed the word-diff on the HTML string,
 * making green highlights track HTML attribute differences rather than
 * prose changes. This module is the seam that keeps the LLM working on
 * text and the editor working on HTML.
 *
 * Pure functions, no React. Tests live in tests/unit/utils/htmlTextBridge.test.js.
 */

const ENTITY_MAP = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&mdash;': '—',
  '&ndash;': '–',
  '&lsquo;': '‘',
  '&rsquo;': '’',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&hellip;': '…',
  '&copy;': '©',
  '&reg;': '®',
  // Zero-width non-joiner — email preheader padding; must vanish in text.
  '&zwnj;': ''
};

const decodeEntities = (text) =>
  text.replace(/&[a-z]+;|&#\d+;/gi, (match) => ENTITY_MAP[match] || match);

// Numeric character references (&#8217; &#x2019;) — the named map above is
// deliberately small; email bodies from Quill and templates carry these.
// Runs on the RAW html (before tag stripping and named decoding) so an
// escaped literal like "&amp;#8217;" is not decoded twice. Markup characters
// are re-expressed as named entities so they cannot become tags; control
// characters and surrogates stay as written (a lone surrogate would break
// UTF-8 encoding downstream).
const MARKUP_AS_NAMED = { 60: '&lt;', 62: '&gt;', 38: '&amp;', 34: '&quot;' };
const decodeNumericEntities = (text) =>
  text.replace(/&#(x[0-9a-f]+|\d+);/gi, (match, ref) => {
    const code = ref[0].toLowerCase() === 'x' ? parseInt(ref.slice(1), 16) : parseInt(ref, 10);
    if (!Number.isFinite(code) || code > 0x10ffff) return match;
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f) || (code >= 0xd800 && code <= 0xdfff)) return match;
    return MARKUP_AS_NAMED[code] || String.fromCodePoint(code);
  });

/**
 * Convert HTML to readable plain text.
 *
 * - Block tags (\</p\>, \</h*\>, \</div\>, \</li\>) become paragraph or line
 *   breaks so structure isn't lost.
 * - <br> becomes a single newline.
 * - <li> becomes a bullet ("• ") so lists stay legible.
 * - Inline tags (<a>, <span>, <strong>, etc.) are stripped, link text kept.
 * - Common HTML entities are decoded.
 * - Excessive whitespace collapses to at most one blank line.
 *
 * Defensive against `null` / `undefined` / non-string inputs.
 */
export function htmlToPlainText(html) {
  if (html == null) return '';
  let text = String(html);

  // Strip <script> / <style> / <head> blocks (and their contents) — never
  // useful as visible text; full email documents carry <head><title> that
  // must not leak into the text part or inbox preview.
  text = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  text = text.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  text = text.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');

  // Convert structural tags to whitespace BEFORE stripping the rest.
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/p>/gi, '\n\n');
  text = text.replace(/<\/(h[1-6]|div|section|article|table|tr|blockquote|ul|ol)>/gi, '\n\n');
  text = text.replace(/<li[^>]*>/gi, '• ');
  text = text.replace(/<\/li>/gi, '\n');

  // Strip every remaining tag.
  text = text.replace(/<[^>]+>/g, '');

  // Decode common entities.
  text = decodeEntities(text);

  // Normalize whitespace: collapse runs of 3+ newlines to 2 (paragraph
  // break), trim trailing spaces on lines, drop leading/trailing whitespace.
  text = text.replace(/[ \t]+\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.trim();

  return text;
}

/**
 * Text/plain renderer for OUTGOING MAIL (#471). Same structure rules as
 * htmlToPlainText, plus what a text-only reader needs and the style
 * round-trip does not:
 *
 * - Links keep their URL: `<a href="U">T</a>` → `T (U)` (just `U` when the
 *   text already is the URL). http(s) and mailto only; anchors, javascript:
 *   and empty hrefs render as plain text. The CAN-SPAM footer's Unsubscribe
 *   link stays actionable without HTML.
 * - Table cells are tab-separated and rows line-separated, so a pasted
 *   market table does not squash into one token.
 * - <hr> is a paragraph break; numeric entities are decoded.
 */
export function htmlToEmailText(html) {
  if (html == null) return '';
  let text = decodeNumericEntities(String(html));

  text = text.replace(/<a\b[^>]*href\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi, (match, _q, href, inner) => {
    const url = decodeEntities(href.trim());
    if (!/^(https?:\/\/|mailto:)/i.test(url)) return inner;
    const label = inner.replace(/<[^>]+>/g, '').trim();
    if (!label || decodeEntities(label) === url) return url;
    return `${inner} (${url})`;
  });

  // Source newlines between block tags (marked emits "</li>\n<li>") would
  // otherwise read as extra blank lines; inline whitespace is left alone.
  text = text.replace(/(<\/(?:li|p|h[1-6]|div|tr|td|th|ul|ol|table|thead|tbody|blockquote)>)\s*\n\s*(?=<)/gi, '$1');
  text = text.replace(/(<(?:ul|ol|table|tbody|thead|tr)\b[^>]*>)\s*\n\s*(?=<)/gi, '$1');

  text = text.replace(/<\/t[dh]>\s*(?=<t[dh]\b)/gi, '\t');
  text = text.replace(/<\/t[dh]>/gi, '');
  text = text.replace(/<\/tr>/gi, '\n');
  text = text.replace(/<hr\s*\/?>/gi, '\n\n');

  return htmlToPlainText(text);
}

/**
 * Convert plain text back to safe HTML for the Quill editor.
 *
 * - Splits on blank lines (\\n\\n) and wraps each block in <p>.
 * - Single newlines within a paragraph become <br>.
 * - Special characters (<, >, &) are escaped so user-typed text can't
 *   smuggle markup or scripts back in via the round trip.
 *
 * Defensive against `null` / `undefined` / non-string inputs.
 */
export function plainTextToHtml(text) {
  if (text == null) return '';
  const str = String(text);
  if (!str) return '';

  const escape = (s) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  return str
    .split(/\n{2,}/)
    .map((para) => `<p>${escape(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}
